import React, { useEffect, useState, Component } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { exchangeStravaCode, initiateStravaOAuth } from '../../services/stravaService';
import { supabase } from '../../lib/supabase';

// ── Error Boundary ────────────────────────────────────────────────────────────
class StravaCallbackErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('[Strava OAuth] ErrorBoundary caught an uncaught error:', error);
    console.error('[Strava OAuth] Component stack:', info?.componentStack);
  }

  render() {
    if (this.state?.hasError) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl p-8 max-w-sm w-full text-center shadow-lg">
            <div className="w-16 h-16 rounded-2xl bg-orange-500 flex items-center justify-center mx-auto mb-5">
              <Icon name="Activity" size={32} className="text-white" />
            </div>
            <div className="w-12 h-12 rounded-full bg-destructive/15 flex items-center justify-center mx-auto mb-4">
              <Icon name="XCircle" size={28} className="text-destructive" />
            </div>
            <h2 className="text-lg font-semibold text-foreground mb-2">Connection Failed</h2>
            <p className="text-sm text-muted-foreground mb-5">
              {this.state?.error?.message || 'An unexpected error occurred during Strava connection.'}
            </p>
            <button
              onClick={() => window.location.href = '/profile-settings'}
              className="flex items-center justify-center gap-2 w-full bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-xl px-4 py-2.5 transition-colors mb-2"
            >
              <Icon name="RefreshCw" size={15} />
              Retry from Settings
            </button>
          </div>
        </div>
      );
    }
    return this.props?.children;
  }
}

// ── Debug Step Tracker ────────────────────────────────────────────────────────
const STEPS = [
  { id: 'params',   label: 'Reading URL parameters' },
  { id: 'session',  label: 'Retrieving session data' },
  { id: 'exchange', label: 'Exchanging tokens with Strava' },
  { id: 'save',     label: 'Saving connection' },
];

const StepIcon = ({ status }) => {
  if (status === 'done')    return <Icon name="CheckCircle2" size={14} className="text-success flex-shrink-0" />;
  if (status === 'error')   return <Icon name="XCircle"      size={14} className="text-destructive flex-shrink-0" />;
  if (status === 'active')  return <div className="w-3.5 h-3.5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />;
  return <div className="w-3.5 h-3.5 rounded-full border border-border flex-shrink-0" />;
};

/**
 * Converts a raw error message (which may contain JSON) into a human-readable string.
 * Prevents raw JSON like {"message":"Bad Request","errors":[...]} from being shown.
 */
function parseErrorMessage(raw) {
  if (!raw) return 'An unexpected error occurred. Please try again.';
  // If it looks like JSON, try to extract a readable message
  const trimmed = String(raw)?.trim();
  if (trimmed?.startsWith('{') || trimmed?.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed?.errors?.length > 0) {
        const e = parsed?.errors?.[0];
        return `Strava rejected the request: "${e?.field}" is ${e?.code}. Please check your Strava app settings.`;
      }
      if (parsed?.message) return parsed?.message;
      if (parsed?.error) return parsed?.error;
    } catch (_) {
      // Not valid JSON — fall through
    }
  }
  // Strip any embedded JSON from the message (e.g. "Token exchange failed: {...}")
  const jsonMatch = trimmed?.match(/^(.*?)(\{.*\}|\[.*\])(.*)$/s);
  if (jsonMatch) {
    try {
      const embedded = JSON.parse(jsonMatch?.[2]);
      if (embedded?.errors?.length > 0) {
        const e = embedded?.errors?.[0];
        return `Strava rejected the request: "${e?.field}" is ${e?.code}. Please check your Strava app settings.`;
      }
      if (embedded?.message) return embedded?.message;
    } catch (_) {
      // Not parseable — return cleaned prefix
      const prefix = jsonMatch?.[1]?.trim();
      if (prefix) return prefix;
    }
  }
  return trimmed;
}

// ── Main Callback Component ───────────────────────────────────────────────────
const StravaCallbackInner = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // 'processing' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const [errorDetails, setErrorDetails] = useState(null);
  const [debugSteps, setDebugSteps] = useState(
    STEPS?.reduce((acc, s) => ({ ...acc, [s?.id]: 'pending' }), {})
  );
  const [debugDetails, setDebugDetails] = useState([]);
  const [showDebug, setShowDebug] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const addDebugLine = (msg, type = 'info') => {
    const ts = new Date()?.toISOString()?.split('T')?.[1]?.replace('Z', '');
    setDebugDetails(prev => [...(prev || []), { ts, msg, type }]);
    if (type === 'error') {
      console.error(`[Strava OAuth Callback] ${msg}`);
    } else {
      console.log(`[Strava OAuth Callback] ${msg}`);
    }
  };

  const setStep = (stepId, stepStatus) => {
    setDebugSteps(prev => ({ ...(prev || {}), [stepId]: stepStatus }));
  };

  const showError = (message, details) => {
    const readable = parseErrorMessage(message);
    setStatus('error');
    setErrorMessage(readable);
    setErrorDetails(details || { code: 'error', description: readable });
  };

  const handleRetry = async () => {
    setRetrying(true);
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const userId = user?.id || localStorage.getItem('strava_oauth_user_id') || sessionStorage.getItem('strava_oauth_user_id');
      if (userId) {
        initiateStravaOAuth(userId);
      } else {
        navigate('/profile-settings');
      }
    } catch {
      navigate('/profile-settings');
    }
  };

  useEffect(() => {
    const handleCallback = async () => {
      try {
        console.group('[Strava OAuth] StravaCallback mounted');

        // ── Step 1: Read URL params ──────────────────────────────────────────
        setStep('params', 'active');
        const code  = searchParams?.get('code');
        const error = searchParams?.get('error');
        const scope = searchParams?.get('scope');
        const state = searchParams?.get('state');

        addDebugLine(`URL params — code: ${code ? code?.substring(0, 8) + '…' : 'MISSING'}, error: ${error || 'none'}, scope: ${scope || 'none'}, state: ${state || 'none'}`);

        // Handle user denial
        if (error === 'access_denied') {
          setStep('params', 'error');
          addDebugLine('User denied Strava access (access_denied)', 'error');
          console.groupEnd();
          showError('You denied access to Strava.', {
            code: 'access_denied',
            description: 'Authorization was not granted. Click "Retry" to try connecting again.',
          });
          return;
        }

        if (error) {
          setStep('params', 'error');
          addDebugLine(`Strava returned error param: "${error}"`, 'error');
          console.groupEnd();
          showError(`Strava returned an error: ${error}`, {
            code: error,
            description: 'Strava rejected the authorization request. This may be a configuration issue with the app.',
          });
          return;
        }

        if (!code) {
          setStep('params', 'error');
          addDebugLine('No authorization code in URL — redirect may have failed', 'error');
          console.groupEnd();
          showError('No authorization code received from Strava.', {
            code: 'missing_code',
            description: 'The redirect URI may be misconfigured. Check that the Authorization Callback Domain in your Strava API settings matches: ' + (window.location?.hostname || ''),
          });
          return;
        }

        setStep('params', 'done');
        addDebugLine('URL params OK — code received');

        // ── Step 2: Retrieve session ─────────────────────────────────────────
        setStep('session', 'active');
        const userId =
          localStorage.getItem('strava_oauth_user_id') ||
          sessionStorage.getItem('strava_oauth_user_id');
        addDebugLine(`strava_oauth_user_id: ${userId || 'NOT FOUND'} (checked localStorage + sessionStorage)`);

        if (!userId) {
          setStep('session', 'error');
          addDebugLine('Session expired or storage cleared — userId missing', 'error');
          console.groupEnd();
          showError('Session expired.', {
            code: 'session_expired',
            description: 'Your session data was lost during the OAuth redirect. Please try connecting again from Profile Settings.',
          });
          return;
        }

        setStep('session', 'done');
        addDebugLine('Session OK — userId retrieved');

        // ── Step 3: Exchange tokens ──────────────────────────────────────────
        setStep('exchange', 'active');
        addDebugLine('Calling Edge Function fitness-oauth-exchange…');

        let result;
        try {
          result = await exchangeStravaCode(code, userId);
        } catch (exchangeErr) {
          setStep('exchange', 'error');
          setStep('save', 'error');
          const rawMsg = exchangeErr?.message || 'Failed to connect Strava. Please try again.';
          addDebugLine(`Token exchange FAILED: ${rawMsg}`, 'error');
          console.error('[Strava OAuth] exchangeStravaCode threw:', exchangeErr);
          console.groupEnd();
          showError(rawMsg, null);
          setShowDebug(true);
          return;
        }

        setStep('exchange', 'done');
        addDebugLine(`Token exchange success — athlete: ${result?.athlete?.firstname || 'unknown'}`);

        // ── Step 4: Cleanup & redirect ───────────────────────────────────
        setStep('save', 'active');
        localStorage.removeItem('strava_oauth_user_id');
        sessionStorage.removeItem('strava_oauth_user_id');
        addDebugLine('Cleaned up localStorage/sessionStorage');
        setStep('save', 'done');
        addDebugLine('All steps complete — redirecting to Activity Tracking');
        console.groupEnd();

        setStatus('success');
        setTimeout(() => navigate('/activity-tracking'), 2000);
      } catch (fatalErr) {
        // Safety net: catch any unhandled error and show proper UI instead of black screen
        console.error('[Strava OAuth] Fatal unhandled error in handleCallback:', fatalErr);
        showError(fatalErr?.message || 'An unexpected error occurred during Strava connection.', {
          code: 'unexpected_error',
          description: 'An unexpected error occurred. Please return to Settings and try again.',
        });
      }
    };

    handleCallback();
  }, []);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="bg-surface border border-border rounded-2xl p-8 max-w-md w-full text-center shadow-lg">
        {/* Strava Logo Area */}
        <div className="w-16 h-16 rounded-2xl bg-orange-500 flex items-center justify-center mx-auto mb-5">
          <Icon name="Activity" size={32} className="text-white" />
        </div>

        {status === 'processing' && (
          <>
            <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-foreground mb-2">Connecting to Strava</h2>
            <p className="text-sm text-muted-foreground mb-5">
              Exchanging authorization tokens, please wait…
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 rounded-full bg-green-500/15 flex items-center justify-center mx-auto mb-4 ring-4 ring-green-500/20">
              <Icon name="CheckCircle2" size={36} className="text-green-500" />
            </div>
            <h2 className="text-xl font-bold text-foreground mb-2">Strava Connected!</h2>
            <p className="text-sm text-muted-foreground mb-3">
              Your Strava account has been successfully linked.
            </p>
            <div className="flex items-center justify-center gap-2 text-xs text-green-600 dark:text-green-400 bg-green-500/10 rounded-lg px-4 py-2.5 mb-1">
              <div className="w-3 h-3 border-2 border-green-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
              <span>Redirecting to Activity Tracking…</span>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-12 h-12 rounded-full bg-destructive/15 flex items-center justify-center mx-auto mb-4">
              <Icon name="XCircle" size={28} className="text-destructive" />
            </div>
            <h2 className="text-lg font-semibold text-foreground mb-2">Connection Failed</h2>

            {/* Error Response Box */}
            <div className="bg-destructive/8 border border-destructive/25 rounded-xl p-4 mb-5 text-left">
              <div className="flex items-start gap-2.5 mb-2">
                <Icon name="AlertTriangle" size={15} className="text-destructive flex-shrink-0 mt-0.5" />
                <span className="text-xs font-semibold text-destructive uppercase tracking-wide">Strava Connection Error</span>
              </div>
              {errorDetails?.code && errorDetails?.code !== 'error' && (
                <div className="mb-2">
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wide font-medium">Error code</span>
                  <p className="text-xs font-mono text-destructive bg-destructive/10 rounded px-2 py-1 mt-0.5 break-all">{errorDetails?.code}</p>
                </div>
              )}
              <div>
                <span className="text-[10px] text-muted-foreground uppercase tracking-wide font-medium">Details</span>
                <p className="text-xs text-foreground/80 mt-0.5 leading-relaxed">
                  {errorDetails?.description || errorMessage || 'An error occurred. Please try again.'}
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-2.5">
              <button
                onClick={handleRetry}
                disabled={retrying}
                className="flex items-center justify-center gap-2 w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white text-sm font-semibold rounded-xl px-4 py-2.5 transition-colors"
              >
                {retrying ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Icon name="RefreshCw" size={15} />
                )}
                {retrying ? 'Redirecting…' : 'Retry Strava Connection'}
              </button>
              <button
                onClick={() => navigate('/profile-settings')}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Back to Settings
              </button>
            </div>
          </>
        )}

        {/* ── Step Progress ─────────────────────────────────────────────── */}
        {(status === 'processing' || status === 'error') && (
          <div className="mt-5 text-left space-y-2">
            {STEPS?.map((step) => {
              const s = debugSteps?.[step?.id];
              return (
                <div key={step?.id} className="flex items-center gap-2.5">
                  <StepIcon status={s} />
                  <span className={`text-xs ${
                    s === 'done'    ? 'text-success' :
                    s === 'error'   ? 'text-destructive' :
                    s === 'active'  ? 'text-foreground font-medium' :
                    'text-muted-foreground'
                  }`}>
                    {step?.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Debug Panel ───────────────────────────────────────────────── */}
        {debugDetails?.length > 0 && (
          <div className="mt-5 text-left">
            <button
              onClick={() => setShowDebug(v => !v)}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors w-full"
            >
              <Icon name={showDebug ? 'ChevronUp' : 'ChevronDown'} size={13} />
              <span>{showDebug ? 'Hide' : 'Show'} debug details ({debugDetails?.length} events)</span>
            </button>

            {showDebug && (
              <div className="mt-2 bg-muted/40 border border-border rounded-lg p-3 max-h-48 overflow-y-auto">
                <div className="space-y-1">
                  {debugDetails?.map((line, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className="text-[10px] text-muted-foreground font-mono flex-shrink-0 mt-0.5">{line?.ts}</span>
                      <span className={`text-[11px] font-mono leading-relaxed break-all ${
                        line?.type === 'error' ? 'text-destructive' : 'text-foreground/80'
                      }`}>
                        {line?.msg}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Exported component wrapped in ErrorBoundary ───────────────────────────────
const StravaCallback = () => (
  <StravaCallbackErrorBoundary>
    <StravaCallbackInner />
  </StravaCallbackErrorBoundary>
);

export default StravaCallback;
